import { lazy, Suspense, useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { AdminPage, AuthCallbackPage } from "./AdminPage";
import { ApiError, geocode, loadOpportunities, requestInvitation, submitInquiry, submitOpportunity } from "./api";
import { copy, languageNames } from "./i18n";
import { LegalContent, legalContent, legalPageMetadata } from "./legal";
import type { Filters, Language, LocalizedText, Opportunity, OpportunityKind, Sector } from "./types";

const MapView = lazy(() => import("./MapView").then((module) => ({ default: module.MapView })));

const languages: Language[] = ["de", "hu", "en"];
const sectors: Sector[] = ["manufacturing", "construction", "it", "logistics", "food", "energy", "other"];
const countries = ["AT", "HU", "DE", "SK", "CZ", "PL", "RO", "SI", "HR"];

function initialLanguage(): Language {
  const stored = window.localStorage.getItem("broker-language");
  if (stored === "de" || stored === "hu" || stored === "en") return stored;
  const browser = navigator.language.slice(0, 2);
  return browser === "hu" || browser === "de" ? browser : "en";
}

function initialInviteToken(): string {
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.slice(1));
  const token = (hashParams.get("invite") ?? url.searchParams.get("invite"))?.trim();
  if (token) {
    url.searchParams.delete("invite");
    hashParams.delete("invite");
    const cleanHash = hashParams.size > 0 ? `#${hashParams.toString()}` : "";
    window.history.replaceState({}, "", `${url.pathname}${url.search}${cleanHash}`);
    if (token.length >= 12 && token.length <= 200) {
      window.sessionStorage.setItem("broker-invite-token", token);
      return token;
    }
  }
  return window.sessionStorage.getItem("broker-invite-token") ?? "";
}

function initialReferralCode(): string {
  const url = new URL(window.location.href);
  const referral = url.searchParams.get("ref")?.trim() ?? "";
  if (referral) {
    url.searchParams.delete("ref");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    if (referral.length <= 100) window.sessionStorage.setItem("broker-referral", referral);
  }
  return referral || window.sessionStorage.getItem("broker-referral") || "";
}

function localized(value: LocalizedText, language: Language): string {
  return value[language] ?? value.en ?? value.de ?? value.hu ?? "";
}

function countryName(code: string, language: Language): string {
  try {
    return new Intl.DisplayNames([language], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

function formatDate(value: string, language: Language): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(language, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(`${value}T12:00:00Z`));
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand${compact ? " brand--compact" : ""}`}>
      <span className="brand-word">B2B<span>Scouts</span></span>
    </span>
  );
}

function Modal({ children, onClose, closeLabel }: { children: ReactNode; onClose: () => void; closeLabel: string }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("modal-open");
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal" role="dialog" aria-modal="true">
        <button className="modal-close" type="button" aria-label={closeLabel} onClick={onClose}>×</button>
        {children}
      </section>
    </div>
  );
}

function PostModal({ language, inviteToken, onClose, onConsumed }: { language: Language; inviteToken: string; onClose: () => void; onConsumed: () => void }) {
  const t = copy[language];
  const [kind, setKind] = useState<OpportunityKind>("capacity");
  const [phase, setPhase] = useState<"form" | "sending" | "success" | "error" | "invite-error">("form");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPhase("sending");
    const form = new FormData(event.currentTarget);
    const country = String(form.get("country"));
    const city = String(form.get("city"));
    try {
      const [longitude, latitude] = await geocode(city, country);
      await submitOpportunity({
        kind,
        title: form.get("title"),
        summary: form.get("summary"),
        locale: language,
        company: form.get("company"),
        website: form.get("website"),
        sector: form.get("sector"),
        country,
        city,
        longitude,
        latitude,
        availableFrom: form.get("availableFrom"),
        volume: form.get("volume"),
        skills: String(form.get("skills") ?? "").split(",").map((item) => item.trim()).filter(Boolean),
        languages: form.getAll("languages"),
        contactName: form.get("contactName"),
        contactEmail: form.get("contactEmail"),
        inviteToken,
        privacy: form.get("privacy") === "on",
        websiteConfirmation: form.get("websiteConfirmation"),
      });
      window.sessionStorage.removeItem("broker-invite-token");
      onConsumed();
      setPhase("success");
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        window.sessionStorage.removeItem("broker-invite-token");
        onConsumed();
        setPhase("invite-error");
      } else {
        setPhase("error");
      }
    }
  }

  return (
    <Modal onClose={onClose} closeLabel={t.card.close}>
      {phase === "success" ? (
        <div className="form-result"><span>✓</span><h2>{t.postForm.successTitle}</h2><p>{t.postForm.successText}</p><button className="button button-primary" onClick={onClose}>{t.card.close}</button></div>
      ) : (
        <>
          <p className="eyebrow"><span />{t.postForm.eyebrow}</p>
          <h2>{t.postForm.title}</h2>
          <p className="modal-intro">{t.postForm.intro}</p>
          <form className="listing-form" onSubmit={onSubmit}>
            <fieldset className="kind-picker">
              <legend>{t.postForm.kind}</legend>
              <label className={kind === "contract" ? "is-active" : ""}><input type="radio" name="kind" value="contract" checked={kind === "contract"} onChange={() => setKind("contract")} /><span className="kind-icon">A</span><b>{t.postForm.contract}</b></label>
              <label className={kind === "capacity" ? "is-active" : ""}><input type="radio" name="kind" value="capacity" checked={kind === "capacity"} onChange={() => setKind("capacity")} /><span className="kind-icon">K</span><b>{t.postForm.capacity}</b></label>
            </fieldset>
            <div className="form-grid">
              <label className="span-2">{t.postForm.titleLabel}<input name="title" required minLength={4} maxLength={120} placeholder={t.postForm.titleHint} /></label>
              <label>{t.postForm.company}<input name="company" required minLength={2} maxLength={120} /></label>
              <label>{t.postForm.sector}<select name="sector" required>{sectors.map((sector) => <option key={sector} value={sector}>{t.sectors[sector]}</option>)}</select></label>
              <label>{t.postForm.country}<select name="country" required defaultValue="HU">{countries.map((country) => <option key={country} value={country}>{countryName(country, language)}</option>)}</select></label>
              <label>{t.postForm.city}<input name="city" required minLength={2} maxLength={100} /></label>
              <label className="span-2">{t.postForm.summary}<textarea name="summary" required minLength={40} maxLength={1500} rows={5} placeholder={t.postForm.summaryHint} /></label>
              <label>{t.postForm.from}<input name="availableFrom" type="date" /></label>
              <label>{t.postForm.volume}<input name="volume" required minLength={2} maxLength={120} placeholder={t.postForm.volumeHint} /></label>
              <label className="span-2">{t.postForm.skills}<input name="skills" maxLength={240} placeholder={t.postForm.skillsHint} /></label>
              <fieldset className="language-picker span-2"><legend>{t.postForm.language}</legend>{languages.map((item) => <label key={item}><input type="checkbox" name="languages" value={item} defaultChecked={item === language} />{languageNames[item]}</label>)}</fieldset>
              <label>{t.postForm.contactName}<input name="contactName" required minLength={2} maxLength={100} autoComplete="name" /></label>
              <label>{t.postForm.email}<input name="contactEmail" type="email" required maxLength={200} autoComplete="email" /></label>
              <label className="span-2">{t.postForm.website}<input name="website" type="url" maxLength={300} placeholder="https://" autoComplete="url" /></label>
              <label className="honeypot" aria-hidden="true">Website confirmation<input name="websiteConfirmation" tabIndex={-1} autoComplete="off" /></label>
              <label className="check-row span-2"><input name="privacy" type="checkbox" required /><span>{t.postForm.privacy}</span></label>
            </div>
            {(phase === "error" || phase === "invite-error") && <p className="form-error" role="alert">{phase === "invite-error" ? t.postForm.inviteError : t.postForm.error}</p>}
            <button className="button button-primary form-submit" disabled={phase === "sending" || phase === "invite-error"}>{phase === "sending" ? t.postForm.sending : t.postForm.submit}<span>↗</span></button>
          </form>
        </>
      )}
    </Modal>
  );
}

function InvitationRequestModal({
  language,
  referralCode,
  onCredential,
  onClose,
}: {
  language: Language;
  referralCode: string;
  onCredential: (credential: string) => void;
  onClose: () => void;
}) {
  const t = copy[language];
  const [phase, setPhase] = useState<"form" | "sending" | "success" | "error">("form");
  const [code, setCode] = useState("");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPhase("sending");
    const form = new FormData(event.currentTarget);
    try {
      await requestInvitation({
        company: form.get("company"),
        contactName: form.get("contactName"),
        email: form.get("email"),
        locale: language,
        website: form.get("website"),
        reason: form.get("reason"),
        referralCode: form.get("referralCode") || undefined,
        privacy: form.get("privacy") === "on",
        websiteConfirmation: form.get("websiteConfirmation"),
      });
      window.sessionStorage.removeItem("broker-referral");
      setPhase("success");
    } catch {
      setPhase("error");
    }
  }

  function useCode() {
    const credential = code.trim().toUpperCase();
    if (credential.length < 12 || credential.length > 200) return;
    window.sessionStorage.setItem("broker-invite-token", credential);
    onCredential(credential);
  }

  return (
    <Modal onClose={onClose} closeLabel={t.card.close}>
      {phase === "success" ? (
        <div className="form-result"><span>✓</span><h2>{t.requestForm.successTitle}</h2><p>{t.requestForm.successText}</p><button className="button button-primary" onClick={onClose}>{t.card.close}</button></div>
      ) : (
        <>
          <p className="eyebrow"><span />{t.requestForm.eyebrow}</p>
          <h2>{t.requestForm.title}</h2>
          <p className="modal-intro">{t.requestForm.intro}</p>
          <div className="invite-code-entry">
            <label>{t.requestForm.codeLabel}<input value={code} onChange={(event) => setCode(event.target.value)} placeholder={t.requestForm.codeHint} autoComplete="off" /></label>
            <button className="button button-outline" type="button" disabled={code.trim().length < 12} onClick={useCode}>{t.requestForm.useCode}</button>
          </div>
          <form className="listing-form" onSubmit={onSubmit}>
            <div className="form-grid">
              <label>{t.requestForm.company}<input name="company" required minLength={2} maxLength={120} autoComplete="organization" /></label>
              <label>{t.requestForm.name}<input name="contactName" required minLength={2} maxLength={100} autoComplete="name" /></label>
              <label>{t.requestForm.email}<input name="email" type="email" required maxLength={200} autoComplete="email" /></label>
              <label>{t.requestForm.website}<input name="website" type="url" maxLength={300} placeholder="https://" autoComplete="url" /></label>
              <label className="span-2">{t.requestForm.reason}<textarea name="reason" required minLength={20} maxLength={1500} rows={5} placeholder={t.requestForm.reasonHint} /></label>
              <label className="span-2">{t.requestForm.referral}<input name="referralCode" defaultValue={referralCode} maxLength={100} autoComplete="off" /></label>
              <label className="honeypot" aria-hidden="true">Website confirmation<input name="websiteConfirmation" tabIndex={-1} autoComplete="off" /></label>
              <label className="check-row span-2"><input name="privacy" type="checkbox" required /><span>{t.requestForm.privacy}</span></label>
            </div>
            {phase === "error" && <p className="form-error" role="alert">{t.requestForm.error}</p>}
            <button className="button button-primary form-submit" disabled={phase === "sending"}>{phase === "sending" ? t.requestForm.sending : t.requestForm.submit}<span>↗</span></button>
          </form>
        </>
      )}
    </Modal>
  );
}

function InquiryModal({ language, opportunity, onClose }: { language: Language; opportunity: Opportunity; onClose: () => void }) {
  const t = copy[language];
  const [phase, setPhase] = useState<"form" | "sending" | "success" | "error">("form");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPhase("sending");
    const form = new FormData(event.currentTarget);
    try {
      await submitInquiry({
        opportunityId: opportunity.id,
        company: form.get("company"), name: form.get("name"), email: form.get("email"), message: form.get("message"),
        privacy: form.get("privacy") === "on", websiteConfirmation: form.get("websiteConfirmation"),
      });
      setPhase("success");
    } catch {
      setPhase("error");
    }
  }

  return (
    <Modal onClose={onClose} closeLabel={t.card.close}>
      {phase === "success" ? (
        <div className="form-result"><span>✓</span><h2>{t.inquiry.successTitle}</h2><p>{t.inquiry.successText}</p><button className="button button-primary" onClick={onClose}>{t.card.close}</button></div>
      ) : (
        <>
          <p className="eyebrow"><span />{t.inquiry.eyebrow}</p>
          <h2>{t.inquiry.title}</h2>
          <p className="modal-intro"><b>{localized(opportunity.title, language)}</b><br />{t.inquiry.intro}</p>
          <form className="listing-form" onSubmit={onSubmit}>
            <div className="form-grid">
              <label>{t.inquiry.company}<input name="company" required minLength={2} maxLength={120} /></label>
              <label>{t.inquiry.name}<input name="name" required minLength={2} maxLength={100} autoComplete="name" /></label>
              <label className="span-2">{t.inquiry.email}<input name="email" type="email" required maxLength={200} autoComplete="email" /></label>
              <label className="span-2">{t.inquiry.message}<textarea name="message" required minLength={20} maxLength={2000} rows={6} placeholder={t.inquiry.messageHint} /></label>
              <label className="honeypot" aria-hidden="true">Website confirmation<input name="websiteConfirmation" tabIndex={-1} autoComplete="off" /></label>
              <label className="check-row span-2"><input name="privacy" type="checkbox" required /><span>{t.inquiry.privacy}</span></label>
            </div>
            {phase === "error" && <p className="form-error" role="alert">{t.inquiry.error}</p>}
            <button className="button button-primary form-submit" disabled={phase === "sending"}>{phase === "sending" ? t.inquiry.sending : t.inquiry.submit}<span>↗</span></button>
          </form>
        </>
      )}
    </Modal>
  );
}

function OpportunityCard({ item, language, selected, onSelect }: { item: Opportunity; language: Language; selected: boolean; onSelect: () => void }) {
  const t = copy[language];
  return (
    <article className={`opportunity-card opportunity-card--${item.kind}${selected ? " is-selected" : ""}`}>
      <button className="card-hitarea" type="button" onClick={onSelect} aria-label={`${t.card.details}: ${localized(item.title, language)}`} />
      <div className="card-topline"><span className="kind-label"><i />{t.kinds[item.kind]}</span><span>{item.city} · {item.country}</span></div>
      <h3>{localized(item.title, language)}</h3>
      <p className="company-line">{item.company}{item.verified && <span title={t.card.verified}>✓</span>}</p>
      <p className="card-summary">{localized(item.summary, language)}</p>
      <div className="card-tags">{item.skills.slice(0, 3).map((skill) => <span key={skill}>{skill}</span>)}</div>
      <div className="card-meta"><span><small>{t.card.from}</small>{formatDate(item.availableFrom, language)}</span><span><small>{t.card.scope}</small>{item.volume}</span><b aria-hidden="true">↗</b></div>
    </article>
  );
}

function DetailCard({ item, language, onClose, onContact }: { item: Opportunity; language: Language; onClose: () => void; onContact: () => void }) {
  const t = copy[language];
  return (
    <aside className="detail-card">
      <button className="detail-close" onClick={onClose} aria-label={t.card.close}>×</button>
      <div className="card-topline"><span className="kind-label"><i />{t.kinds[item.kind]}</span><span>{item.city} · {item.country}</span></div>
      <h3>{localized(item.title, language)}</h3>
      <p className="company-line">{item.company}{item.verified && <span title={t.card.verified}>✓</span>}</p>
      <p>{localized(item.summary, language)}</p>
      <dl><div><dt>{t.card.from}</dt><dd>{formatDate(item.availableFrom, language)}</dd></div><div><dt>{t.card.scope}</dt><dd>{item.volume}</dd></div><div><dt>{t.card.languages}</dt><dd>{item.languages.map((item) => languageNames[item]).join(" · ")}</dd></div></dl>
      <div className="card-tags">{item.skills.map((skill) => <span key={skill}>{skill}</span>)}</div>
      <button className="button button-primary detail-cta" onClick={onContact}>{t.card.contact}<span>↗</span></button>
    </aside>
  );
}

function LegalPage({ type }: { type: "imprint" | "privacy" }) {
  const metadata = legalPageMetadata(type);
  return (
    <div className="legal-shell">
      <header className="legal-header"><a href="/"><Brand /></a><a href="/">← Zur Plattform</a></header>
      <main className="legal-page">
        <p className="eyebrow"><span />{metadata.eyebrow}</p><h1>{metadata.title}</h1>
        <div className="legal-copy"><LegalContent type={type} /></div>
      </main>
    </div>
  );
}

export default function App() {
  const path = window.location.pathname.replace(/\/+$/, "");
  if (path === "/impressum") return <LegalPage type="imprint" />;
  if (path === "/datenschutz") return <LegalPage type="privacy" />;
  if (path === "/admin") return <AdminPage />;
  if (path === "/auth/callback") return <AuthCallbackPage />;

  const [language, setLanguage] = useState<Language>(initialLanguage);
  const [items, setItems] = useState<Opportunity[]>([]);
  const [apiState, setApiState] = useState<"loading" | "ready" | "error">("loading");
  const [reloadVersion, setReloadVersion] = useState(0);
  const [filters, setFilters] = useState<Filters>({ query: "", kind: "all", sector: "all", country: "all" });
  const [selectedId, setSelectedId] = useState<string>();
  const [mobileView, setMobileView] = useState<"list" | "map">("list");
  const [modal, setModal] = useState<"post" | "request" | "inquiry" | null>(null);
  const [inviteToken, setInviteToken] = useState(initialInviteToken);
  const [referralCode] = useState(initialReferralCode);
  const t = copy[language];
  const postLabel = inviteToken ? t.invite.create : t.invite.request;
  const openPost = () => setModal(inviteToken ? "post" : "request");

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = t.meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", t.meta.description);
    window.localStorage.setItem("broker-language", language);
  }, [language, t]);

  useEffect(() => {
    let active = true;
    setItems([]);
    setApiState("loading");
    const timeout = window.setTimeout(() => {
      loadOpportunities(filters)
        .then((loaded) => {
          if (!active) return;
          setItems(loaded);
          setApiState("ready");
        })
        .catch(() => {
          if (!active) return;
          setItems([]);
          setApiState("error");
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [filters, reloadVersion]);

  const filtered = items;
  const hasActiveFilters = Boolean(filters.query.trim()) || filters.kind !== "all" || filters.sector !== "all" || filters.country !== "all";

  useEffect(() => {
    if (selectedId && !filtered.some((item) => item.id === selectedId)) setSelectedId(undefined);
  }, [filtered, selectedId]);

  const selected = items.find((item) => item.id === selectedId);
  const markerLabel = useCallback((item: Opportunity) => `${t.kinds[item.kind]}: ${localized(item.title, language)}, ${item.city}`, [t, language]);
  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => setFilters((current) => ({ ...current, [key]: value }));

  return (
    <>
      <header className="site-header">
        <a href="#top" aria-label="B2B Scouts"><Brand /></a>
        <nav className="desktop-nav" aria-label="Primary navigation"><a href="#market">{t.nav.market}</a><a href="#how">{t.nav.how}</a><a href="#about">{t.nav.about}</a><button className="nav-contact" onClick={openPost}>{postLabel}<span>↗</span></button></nav>
        <div className="language-switch" aria-label="Language">{languages.map((item) => <button key={item} aria-current={item === language ? "true" : undefined} onClick={() => setLanguage(item)}>{languageNames[item]}</button>)}</div>
        <details className="mobile-menu"><summary>{t.nav.menu}<span>＋</span></summary><nav><a href="#market">{t.nav.market}</a><a href="#how">{t.nav.how}</a><a href="#about">{t.nav.about}</a><button onClick={openPost}>{postLabel}</button></nav></details>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow"><span />{t.hero.eyebrow}</p>
            <h1>{t.hero.title}<br /><em>{t.hero.accent}</em></h1>
            <p className="hero-intro">{t.hero.intro}</p>
            <div className="hero-actions"><a className="button button-primary" href="#market">{t.hero.explore}<span>↓</span></a><button className="text-link" onClick={openPost}>{postLabel}<span>↗</span></button></div>
          </div>
          <div className="hero-visual" aria-hidden="true">
            <div className="europe-orbit orbit-a" /><div className="europe-orbit orbit-b" />
            <div className="route route-one"><i /><i /><i /></div><div className="route route-two"><i /><i /></div>
            <div className="hero-point point-vienna"><b>Wien</b><span>48.2082° N</span></div>
            <div className="hero-point point-budapest"><b>Budapest</b><span>47.4979° N</span></div>
            <div className="hero-point point-brno"><b>Brno</b><span>49.1951° N</span></div>
            <div className="visual-stamp">B2B<br /><b>EUROPE</b></div>
          </div>
          <div className="proof-strip">{t.proof.map((proof) => <div key={proof.value}><strong>{proof.value}</strong><span>{proof.label}</span></div>)}</div>
        </section>

        {inviteToken && <aside className="invite-banner"><div><span>✓</span><p><strong>{t.invite.active}</strong>{t.invite.activeText}</p></div><button type="button" onClick={() => setModal("post")}>{t.invite.create}<span>↗</span></button></aside>}

        <section className="market-section" id="market">
          <div className="market-heading">
            <div><p className="eyebrow"><span />{t.board.eyebrow}</p><h2>{t.board.title}</h2></div>
            <p>{t.board.intro}</p>
          </div>
          <div className="market-toolbar">
            <label className="search-box"><span aria-hidden="true">⌕</span><input value={filters.query} onChange={(event) => updateFilter("query", event.target.value)} placeholder={t.board.search} /></label>
            <div className="kind-tabs"><button className={filters.kind === "all" ? "is-active" : ""} onClick={() => updateFilter("kind", "all")}>{t.board.all}</button><button className={filters.kind === "contract" ? "is-active" : ""} onClick={() => updateFilter("kind", "contract")}>{t.board.contracts}</button><button className={filters.kind === "capacity" ? "is-active" : ""} onClick={() => updateFilter("kind", "capacity")}>{t.board.capacities}</button></div>
            <select aria-label={t.board.sector} value={filters.sector} onChange={(event) => updateFilter("sector", event.target.value as Filters["sector"])}><option value="all">{t.board.sector}</option>{sectors.map((sector) => <option key={sector} value={sector}>{t.sectors[sector]}</option>)}</select>
            <select aria-label={t.board.country} value={filters.country} onChange={(event) => updateFilter("country", event.target.value)}><option value="all">{t.board.country}</option>{countries.map((country) => <option key={country} value={country}>{countryName(country, language)}</option>)}</select>
          </div>
          <div className="mobile-view-toggle"><button className={mobileView === "list" ? "is-active" : ""} onClick={() => setMobileView("list")}>{t.board.list}</button><button className={mobileView === "map" ? "is-active" : ""} onClick={() => setMobileView("map")}>{t.board.map}</button></div>
          <div className="market-shell">
            <div className={`results-panel${mobileView === "map" ? " mobile-hidden" : ""}`}>
              <div className="results-head"><strong>{filtered.length} {filtered.length === 1 ? t.board.result : t.board.results}</strong>{apiState === "loading" && <span>{t.board.loading}</span>}{apiState === "error" && <span>{t.board.apiError}</span>}</div>
              <div className="results-list">{filtered.length ? filtered.map((item) => <OpportunityCard key={item.id} item={item} language={language} selected={item.id === selectedId} onSelect={() => { setSelectedId(item.id); setMobileView("map"); }} />) : apiState === "error" ? <div className="empty-state"><p>{t.board.apiError}</p><button type="button" onClick={() => setReloadVersion((value) => value + 1)}>{t.board.retry}</button></div> : apiState === "ready" ? hasActiveFilters ? <p className="empty-state">{t.board.unavailable}</p> : <div className="empty-state empty-state--invite"><strong>{t.invite.emptyTitle}</strong><p>{t.invite.emptyText}</p><button type="button" onClick={() => setModal("request")}>{t.invite.request} ↗</button></div> : <div className="results-loading" aria-label={t.board.loading}><span /></div>}</div>
            </div>
            <div className={`map-panel${mobileView === "list" ? " mobile-hidden" : ""}`}>
              <Suspense fallback={<div className="map-loading" aria-hidden="true"><span /></div>}>
                <MapView items={filtered} selectedId={selectedId} onSelect={setSelectedId} markerLabel={markerLabel} />
              </Suspense>
              <div className="map-legend"><span><i className="contract-dot" />{t.kinds.contract}</span><span><i className="capacity-dot" />{t.kinds.capacity}</span></div>
              {selected && <DetailCard item={selected} language={language} onClose={() => setSelectedId(undefined)} onContact={() => setModal("inquiry")} />}
            </div>
          </div>
        </section>

        <section className="een-reference" aria-labelledby="een-title">
          <div><p className="eyebrow"><span />{t.source.eyebrow}</p><h2 id="een-title">{t.source.title}</h2></div>
          <div><p>{t.source.text}</p><a href="https://een.ec.europa.eu/partnering-opportunities" target="_blank" rel="noreferrer">{t.source.link}<span>↗</span></a><small>{t.source.note}</small></div>
        </section>

        <section className="section process" id="how">
          <div className="section-heading"><p className="eyebrow"><span />{t.how.eyebrow}</p><h2>{t.how.title}</h2></div>
          <ol className="process-grid">{t.how.items.map((item) => <li key={item.number}><span>{item.number}</span><h3>{item.title}</h3><p>{item.text}</p></li>)}</ol>
        </section>

        <section className="bridge" id="about">
          <div className="bridge-map" aria-hidden="true"><span className="map-label map-label-west">Österreich</span><span className="map-label map-label-east">Europa</span><div className="bridge-line" /><i className="bridge-node node-one" /><i className="bridge-node node-two" /><i className="bridge-node node-three" /></div>
          <div className="bridge-content"><p className="eyebrow light"><span />{t.bridge.eyebrow}</p><h2>{t.bridge.title}</h2><p>{t.bridge.text}</p><blockquote>{t.bridge.quote}</blockquote></div>
        </section>

        <section className="dual-cta">
          <article><span className="cta-letter">A</span><p className="eyebrow"><span />B2B</p><h2>{t.cta.needTitle}</h2><p>{t.cta.needText}</p><button className="button button-outline" onClick={openPost}>{postLabel}<span>↗</span></button></article>
          <article><span className="cta-letter">K</span><p className="eyebrow light"><span />B2B</p><h2>{t.cta.offerTitle}</h2><p>{t.cta.offerText}</p><button className="button button-light" onClick={openPost}>{postLabel}<span>↗</span></button></article>
        </section>
      </main>

      <footer className="site-footer"><div><Brand compact /><p>{t.footer.line}</p></div><nav><a href="#market">{t.footer.market}</a><button onClick={openPost}>{postLabel}</button><a href="/impressum/">{t.footer.imprint}</a><a href="/datenschutz/">{t.footer.privacy}</a><a href="/admin/">Admin</a></nav><p>© {new Date().getFullYear()} {legalContent.provider.legalName}</p></footer>

      {modal === "post" && <PostModal language={language} inviteToken={inviteToken} onClose={() => setModal(null)} onConsumed={() => setInviteToken("")} />}
      {modal === "request" && <InvitationRequestModal language={language} referralCode={referralCode} onClose={() => setModal(null)} onCredential={(credential) => { setInviteToken(credential); setModal("post"); }} />}
      {modal === "inquiry" && selected && <InquiryModal language={language} opportunity={selected} onClose={() => setModal(null)} />}
    </>
  );
}
